/**
 * APPOINTMENT-BOOK-V1 -- the Appointments PLANNING screen.
 *
 * A READ-ONLY, single-day, date-navigable, per-clinic calendar: per-doctor
 * columns (plus a leading Unassigned lane), IST time bands from the clinic's
 * CONFIGURED booking window, and one block per appointment carrying its time,
 * patient label, and shared status chip.
 *
 * PLANNING, NOT OPERATIONS. Today (/walkin + ClinicDayBoard) stays the
 * operations surface: arrivals, check-ins, the action strip, ops-status,
 * payment doors. This screen answers "what does the day LOOK like, for any
 * date" and writes nothing. There is NO action strip here by design.
 *
 * ONE READ, ONE FENCE: GET /api/appointments/today?view=board&date=YYYY-MM-DD
 * -- the SAME computation, the SAME DAY_BOARD_ROLES audience fence, the SAME
 * clinic fence, the SAME 100-row cap, the SAME doctor-only clinical fence.
 * The date seam moves WHICH day is read; it widens no field, no role, and no
 * clinic. Backend route auth remains the security boundary.
 *
 * ONE-RULE-SOURCE: the time bands come from the server-resolved
 * booking.schedule_rules window (scheduleWindow on the board wire). This
 * surface keeps NO client-side copy of clinic hours.
 *
 * NO SECOND OWNER: status labels + tones render through the shared StatusChip
 * canon (this surface mints NO status-label map and NO tone map); the patient
 * label reuses dayBoardPatientLabel; the row-cap honesty line reuses the day
 * board's exported copy; the block-open door is the EXISTING LeadQuickView --
 * no new drawer, no new modal, no new shared primitive.
 *
 * BLOCK CONTENT IS A CLOSED LIST: time, patient label, status chip. No
 * booking-channel icon (no product writer exists -- D-26 owns it), no
 * doctor-profile facts beyond the roster name + specialty
 * (DOCTOR-PROFILES-V1 owns photo / languages / experience / bio and the
 * hover card), no money marker, no ops-status, no intake chip.
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): the derivations live
 * in ./appointment-calendar-view.ts, AppointmentCalendarView renders from
 * props, and the default container wires state + the fetch.
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';

import { fetchTodayAppointments } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { useClinicLens, clinicOptionLabel, type ClinicOption } from '@/dashboard/clinic-context';
import type {
  ClinicRosterDoctor,
  ClinicScheduleWindow,
  DashboardMe,
  TodayAppointment,
} from '@/dashboard/types';
import {
  ClinicScopeLabel,
  DateCommandBar,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusChip,
  dayScopeRequest,
  useDayScope,
} from '@/dashboard/ui';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import {
  DAY_READ_ROW_CAP,
  DAY_READ_TRUNCATED_COPY,
  canSeeDayBoard,
  dayBoardPatientLabel,
  rowOpenKind,
} from '@/dashboard/appointments/ClinicDayBoard';
import {
  CALENDAR_MOBILE_DOCTOR_FIELD_LABEL,
  CALENDAR_SPECIALTY_UNSET_LABEL,
  buildCalendarGrid,
  calendarCellKey,
  calendarDisplayBands,
  calendarStackEntries,
  type CalendarBlock,
  type CalendarGrid,
} from '@/dashboard/appointments/appointment-calendar-view';

/*
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (Section C verdict: KEEP + RENAME).
 * The door survives on evidence -- the per-doctor lane grid, the configured
 * time bands, and the roster columns for doctors with zero bookings are jobs
 * the day-scoped Home cannot do -- but "Appointments" named the ROWS, which
 * Home now carries for ANY day. The name now says the surviving job, and the
 * nav label (nav-config.ts) says the same word: one label truth.
 *
 * The subtitle also had to stop saying "Actions stay on Today": Home is no
 * longer only today. Actions stay on HOME, where the day board carries them
 * for the CURRENT day (and lawfully withholds them off it).
 */
export const CALENDAR_TITLE = 'Schedule';
export const CALENDAR_EYEBROW = 'Planning';
export const CALENDAR_SUBTITLE =
  'One clinic, one day - who is booked with which doctor. Actions stay on Home.';
/** FD-ROSTER (R-R1..R-R6 banked 2026-08-03): the honest availability seam --
 *  the grid shows the ROSTER; availability is not product data yet
 *  (ROSTER-V1's requirement). Never a control (SEAM law). */
export const CALENDAR_AVAILABILITY_NOTE =
  'Doctor availability is not tracked here yet - columns show the clinic roster. Booking is never blocked on availability.';

export const CALENDAR_EMPTY_TITLE = 'No appointments on this day';
export const CALENDAR_EMPTY_BODY =
  'Pick another date, or book from the lead workbench.';
export const CALENDAR_ERROR_TITLE = 'Could not load this day';
export const CALENDAR_LOADING_LABEL = 'Loading the day...';
/* 119-CLINIC-OPERATING-SPINE-V1 Phase 3 (gap G8, founder ruling item 2).
 * TWO DIFFERENT SITUATIONS WERE WEARING ONE SENTENCE. This state used to read
 * "This account is not assigned to a clinic" for BOTH causes, which is true of
 * a staff account with no clinic row and FALSE of an owner who reaches every
 * clinic in the org and simply has not picked one. Phase 2 created that second
 * cause deliberately, by refusing to drop an owner onto an arbitrary branch of
 * 119 -- so the state it created has to say what it means.
 * The owner's version names the situation AND the way out; it is a designed
 * state, not a blank screen. */
export const CALENDAR_NO_CLINIC_TITLE = 'No clinic in your scope';
export const CALENDAR_NO_CLINIC_BODY =
  'This account is not assigned to a clinic, so there is no day to plan.';
export const CALENDAR_PICK_CLINIC_TITLE = 'Choose a clinic to plan its day';
export const CALENDAR_PICK_CLINIC_BODY =
  'You can reach every clinic in the organisation. Pick one in the clinic ' +
  'selector at the top of the page and its day appears here.';
export const CALENDAR_UNPLACED_TITLE = 'Time not readable';
export const CALENDAR_CLINIC_LABEL = 'Clinic';
/*
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (B1 convergence): the four local day
 * control strings (CALENDAR_PREV_COPY / CALENDAR_NEXT_COPY /
 * CALENDAR_TODAY_COPY / CALENDAR_DATE_LABEL) are RETIRED here -- the DATE
 * COMMAND BAR is the ONE copy owner (DAY_BAR_* in ui/DateCommandBar.tsx), so
 * this surface cannot become a second home for day words. The retired
 * "Date (IST)" field label becomes the market-neutral "Selected date": the bar
 * is shared by every market (US-ready from day one), and the clinic-day
 * definition lives in the day-scope primitive and the SERVER's istDayWindow,
 * never in a control label.
 */

// --- The route guard (ZERO new authority) ---------------------------------

export type CalendarAccessDecision =
  | { kind: 'wait' }
  | { kind: 'redirect'; to: string }
  | { kind: 'render' };

/**
 * The pure route decision, mirroring decideWorkboardAccess: a ROLE-ONLY gate
 * keyed off canSeeDayBoard -- the EXISTING frontend mirror of the backend
 * DAY_BOARD_ROLES set. No new role set is minted here, no /me signal, no
 * feature flag, no new nav section (so no backend mirror / drift-test
 * change). Backend route auth remains the security boundary; this is UX
 * cleanliness so a call_center_agent / patient never sees a blocked card.
 */
export function decideAppointmentCalendarAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): CalendarAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) return { kind: 'redirect', to: '/login' };
  if (!canSeeDayBoard(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

export interface RequireAppointmentCalendarViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  readonly children?: React.ReactNode;
}

/** Pure presentational guard view - testable via react-dom/server. */
export function RequireAppointmentCalendarView({
  loading,
  error,
  me,
  children,
}: RequireAppointmentCalendarViewProps): JSX.Element | null {
  const decision = decideAppointmentCalendarAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export function RequireAppointmentCalendar(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) return <Navigate to="/login" replace />;
  return (
    <RequireAppointmentCalendarView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}

// --- The pure view --------------------------------------------------------

export interface AppointmentCalendarViewProps {
  /** The planning date, 'YYYY-MM-DD' (the clinic day). */
  readonly date: string;
  /** The clinic's today, injected -- the DATE COMMAND BAR reads no clock, so
   *  a rendered calendar is deterministic and pinnable. */
  readonly today: string;
  readonly onDateChange: (date: string) => void;
  /** Step by whole clinic days (-1 / +1) and jump to today. */
  readonly onStepDay: (deltaDays: number) => void;
  readonly onToday: () => void;
  /** The caller's role -- decides the block-open door only (UX, never auth). */
  readonly role: string;
  readonly loading: boolean;
  readonly error: string | null;
  readonly rows: readonly TodayAppointment[];
  readonly clinicDoctors: readonly ClinicRosterDoctor[];
  readonly scheduleWindow?: ClinicScheduleWindow | null;
  /** The caller's verified clinic options. The switcher renders ONLY when
   *  this carries more than one clinic (a single-clinic caller sees none). */
  /** The clinic this board is SHOWING, already a human label (never an id).
   *  null when the caller has resolved no clinic -- the board then renders its
   *  designed empty state rather than a nameless grid. */
  readonly shownClinicLabel: string | null;
  /** G8: TRUE when the caller REACHES at least one clinic. Distinguishes "you
   *  have not picked one yet" from "your account has none" -- two different
   *  situations that must not share a sentence. */
  readonly canReachAnyClinic: boolean;
  /** The clinic the read actually resolved (null = caller has no clinic). */
  readonly activeClinicId: number | null;
  readonly onOpenBlock: (row: TodayAppointment) => void;
}

function BlockButton({
  block,
  clickable,
  onOpen,
}: {
  readonly block: CalendarBlock;
  readonly clickable: boolean;
  readonly onOpen: () => void;
}): JSX.Element {
  // T-35 / accessibility law: the tone NEVER carries meaning alone -- the
  // status chip prints its own text token next to every block, and the time
  // and patient label are plain text. The block reads correctly in greyscale.
  const body = (
    <>
      <span className="ac-calendar__block-time">{block.timeLabel || '--:--'}</span>
      <span className="ac-calendar__block-patient">{block.patientLabel}</span>
      <StatusChip domain="appointment" status={block.status} />
    </>
  );
  if (!clickable) {
    return (
      <div
        className="ac-calendar__block"
        data-testid={`ac-calendar-block-${block.appointmentId}`}
      >
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="ac-calendar__block ac-calendar__block--open"
      data-testid={`ac-calendar-block-${block.appointmentId}`}
      onClick={onOpen}
    >
      {body}
    </button>
  );
}

export function AppointmentCalendarView({
  date,
  today,
  onDateChange,
  onStepDay,
  onToday,
  role,
  loading,
  error,
  rows,
  clinicDoctors,
  scheduleWindow,
  shownClinicLabel,
  canReachAnyClinic,
  activeClinicId,
  onOpenBlock,
}: AppointmentCalendarViewProps): JSX.Element {
  const grid: CalendarGrid = buildCalendarGrid({
    rows,
    clinicDoctors,
    ...(scheduleWindow !== undefined ? { scheduleWindow } : {}),
    patientLabel: dayBoardPatientLabel,
  });
  // FD-ROSTER: the honesty note renders whenever roster columns exist (the
  // grid could otherwise read as an availability claim).
  const columnsHaveDoctors = clinicDoctors.length > 0;
  const stack = calendarStackEntries(grid);
  const rowById = new Map(rows.map((r) => [r.id, r]));
  const openableIds = new Set(
    rows.filter((r) => rowOpenKind(role, r) === 'lead_quickview').map((r) => r.id),
  );

  const renderBlock = (block: CalendarBlock): JSX.Element => (
    <BlockButton
      key={block.appointmentId}
      block={block}
      clickable={openableIds.has(block.appointmentId)}
      onOpen={() => {
        const row = rowById.get(block.appointmentId);
        if (row) onOpenBlock(row);
      }}
    />
  );

  return (
    <section className="ac-calendar" data-testid="ac-calendar">
      <PageHeader
        title={CALENDAR_TITLE}
        eyebrow={CALENDAR_EYEBROW}
        subs={[
          <span key="sub" data-testid="ac-calendar-subtitle">
            {CALENDAR_SUBTITLE}
          </span>,
        ]}
        testId="ac-calendar-header"
      />

      <div className="ac-calendar__controls">
        {/* DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (B1 convergence): the local
            three-button stepper + the local date field + the local day label
            are REPLACED by THE ONE registered day control. Same four moves,
            same clinic-day arithmetic (the primitive owns the implementation
            this surface used to define), now identical to the front-desk Home
            day board -- one control grammar product-wide.

            The CLINIC SWITCHER rides the bar's lawful context slot: it is
            rendered ONLY for a multi-clinic scope (a single-clinic caller
            sees no switcher at all) and narrows WITHIN server-verified scope
            -- never an authorization concept, and never minted by the bar. */}
        {/* CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D1): compact is the
            standard for role-home AND day-board surfaces, so the clinic day
            board takes the same joined-pill grammar the two role homes now
            speak. One prop; the grammar itself lives with the variant in the
            shared layer (C3). The four moves, the day state, the single
            visible date and the lawful context slot below are unchanged. */}
        <DateCommandBar
          date={date}
          today={today}
          onSelect={onDateChange}
          onStep={onStepDay}
          onToday={onToday}
          label="Planning day"
          variant="compact"
        >
          {/* 119-CLINIC-OPERATING-SPINE-V1 (Phase 2): the calendar's OWN clinic
              select is gone. It was a second control for a value the header
              lens already owns, and it forked -- seeded once, never re-synced.
              The clinic this board is showing is NAMED below through THE ONE
              scope primitive; switching clinics is the header lens, once, for
              every surface. */}
          {shownClinicLabel !== null ? (
            <ClinicScopeLabel
              label={shownClinicLabel}
              testId="ac-calendar-clinic"
            />
          ) : null}
        </DateCommandBar>

        {/* RECEPTION-OS-BUILD-V1 (FD-ROSTER amendment, Deepak R-R1..R-R6
            banked 2026-08-03; the ROSTER RULING stands): the columns are the
            clinic ROSTER, never an availability claim -- availability lives
            in the COC's Excel today (R-R1) and its product model is
            ROSTER-V1's. TEXT only (SEAM law); R-R6: booking is never
            blocked on availability. */}
        {columnsHaveDoctors ? (
          <p
            className="ac-calendar__note"
            data-testid="ac-calendar-availability-note"
          >
            {CALENDAR_AVAILABILITY_NOTE}
          </p>
        ) : null}
      </div>

      {loading ? (
        <LoadingState
          label={CALENDAR_LOADING_LABEL}
          lines={3}
          testId="ac-calendar-loading"
        />
      ) : error !== null ? (
        <ErrorState
          title={CALENDAR_ERROR_TITLE}
          detail={error}
          testId="ac-calendar-error"
        />
      ) : activeClinicId === null ? (
        /* G8: which of the two causes is this? A caller who REACHES clinics
           but has not picked one is told how to pick; a caller who reaches
           none is told the truth about their account. */
        canReachAnyClinic ? (
          <EmptyState
            icon={<CalendarDays size={18} aria-hidden="true" />}
            title={CALENDAR_PICK_CLINIC_TITLE}
            body={CALENDAR_PICK_CLINIC_BODY}
            testId="ac-calendar-pickclinic"
          />
        ) : (
          <EmptyState
            icon={<CalendarDays size={18} aria-hidden="true" />}
            title={CALENDAR_NO_CLINIC_TITLE}
            body={CALENDAR_NO_CLINIC_BODY}
            testId="ac-calendar-noclinic"
          />
        )
      ) : (
        <>
          {/* The read caps at DAY_READ_ROW_CAP rows (earliest win) -- the
              day board's OWN honesty line, reused, never a second cap copy. */}
          {rows.length >= DAY_READ_ROW_CAP ? (
            <p className="ac-calendar__note" data-testid="ac-calendar-truncated">
              {DAY_READ_TRUNCATED_COPY}
            </p>
          ) : null}

          {rows.length === 0 && grid.bands.length === 0 ? (
            <EmptyState
              icon={<CalendarDays size={18} aria-hidden="true" />}
              title={CALENDAR_EMPTY_TITLE}
              body={CALENDAR_EMPTY_BODY}
              testId="ac-calendar-empty"
            />
          ) : (
            <>
              {rows.length === 0 ? (
                <p className="ac-calendar__note" data-testid="ac-calendar-emptyday">
                  {CALENDAR_EMPTY_TITLE}
                </p>
              ) : null}

              {/* DESKTOP: the per-doctor grid. */}
              <div className="ac-calendar__gridwrap">
                <table className="ac-calendar__grid" data-testid="ac-calendar-grid">
                  <thead>
                    <tr>
                      <th scope="col" className="ac-calendar__timehead">
                        Time
                      </th>
                      {grid.columns.map((column) => (
                        <th
                          key={column.key}
                          scope="col"
                          data-testid={`ac-calendar-col-${column.key}`}
                        >
                          <span className="ac-calendar__doctor">{column.name}</span>
                          {column.doctorId !== null ? (
                            <span className="ac-calendar__specialty">
                              {column.specialty ?? CALENDAR_SPECIALTY_UNSET_LABEL}
                            </span>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* FD-43 (RECEPTION-FIX-AND-POLISH-BATCH-V1): runs of
                        consecutive EMPTY slots collapse into ONE thin gap
                        row naming the span -- enterprise calendar rhythm; a
                        9am-8pm window stops rendering forty empty rows.
                        Occupied bands render exactly as before. */}
                    {calendarDisplayBands(grid).map((entry) =>
                      entry.kind === 'gap' ? (
                        <tr
                          key={entry.key}
                          className="ac-calendar__gaprow"
                          data-testid={`ac-calendar-gap-${entry.key}`}
                        >
                          <td colSpan={grid.columns.length + 1}>
                            {entry.fromLabel} - {entry.toLabel}: open ({entry.bandCount} slots)
                          </td>
                        </tr>
                      ) : (
                        <tr
                          key={entry.band.key}
                          data-testid={`ac-calendar-band-${entry.band.key}`}
                        >
                          <th scope="row" className="ac-calendar__timecell">
                            {entry.band.label}
                          </th>
                          {grid.columns.map((column) => {
                            const blocks =
                              grid.cells.get(
                                calendarCellKey(entry.band.key, column.key),
                              ) ?? [];
                            return (
                              <td
                                key={column.key}
                                data-testid={`ac-calendar-cell-${entry.band.key}-${column.key}`}
                              >
                                {blocks.map(renderBlock)}
                              </td>
                            );
                          })}
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>

              {/* MOBILE (<=720px): the SAME blocks in ONE time-ordered column
                  with the doctor as a labeled field -- never a horizontal
                  doctor-column scroll on a phone. CSS swaps the two. */}
              <ul className="ac-calendar__stack" data-testid="ac-calendar-stack">
                {stack.map((entry) => (
                  <li
                    key={entry.block.appointmentId}
                    className="ac-calendar__stack-item"
                    data-testid={`ac-calendar-stack-${entry.block.appointmentId}`}
                  >
                    {renderBlock(entry.block)}
                    <p className="ac-calendar__stack-doctor">
                      <span className="ac-calendar__stack-doctor-label">
                        {CALENDAR_MOBILE_DOCTOR_FIELD_LABEL}
                      </span>
                      <span>{entry.doctorLabel}</span>
                    </p>
                  </li>
                ))}
              </ul>

              {/* An unreadable appointment_time is shown, never dropped. */}
              {grid.unplacedBlocks.length > 0 ? (
                <div
                  className="ac-calendar__unplaced"
                  data-testid="ac-calendar-unplaced"
                >
                  <p className="ac-calendar__note">{CALENDAR_UNPLACED_TITLE}</p>
                  {grid.unplacedBlocks.map(renderBlock)}
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </section>
  );
}

// --- The container --------------------------------------------------------

type CalendarState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      rows: TodayAppointment[];
      clinicDoctors: readonly ClinicRosterDoctor[];
      scheduleWindow: ClinicScheduleWindow | null;
      activeClinicId: number | null;
    }
  | { kind: 'error'; message: string };

export default function AppointmentCalendarPage(): JSX.Element {
  const meCtx = useDashboardMeContext();
  const clinicCtx = useClinicLens();
  const role = meCtx?.me?.role ?? '';
  const clinicOptions: readonly ClinicOption[] = meCtx?.me?.clinicOptions ?? [];

  /*
   * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (B1 convergence, findings F1/F2/F3):
   * the planning day moved from component state into the URL through THE ONE
   * day-scope hook. The verified gaps close together -- a refresh now
   * restores the chosen day instead of silently snapping back to today, a
   * planning day is shareable and deep-linkable, and browser back / forward
   * walks the date history. The read, the fences and the row cap are
   * unchanged: the day is still just a parameter to the SAME fenced call.
   */
  const day = useDayScope();
  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2).
  // This was `useState(() => clinicCtx?.selectedClinicId)` -- seeded ONCE at
  // mount and never re-synced, so switching clinics in the header left the
  // calendar showing the old clinic's day while the header named the new one.
  // The lens is now read directly: there is no second copy to drift.
  const clinicId = clinicCtx?.selectedClinicId ?? null;
  // The clinic being shown, named. Falls back to the read's own resolved
  // clinic so the board never renders a grid with no clinic on it.
  const shownClinicLabel: string | null = (() => {
    const opt = clinicOptions.find((o) => o.clinicId === clinicId) ?? null;
    if (opt !== null) return clinicOptionLabel(opt);
    return clinicCtx !== null && clinicCtx.selectedLabel !== ''
      ? clinicCtx.selectedLabel
      : null;
  })();
  const [state, setState] = useState<CalendarState>({ kind: 'loading' });
  const [openLead, setOpenLead] = useState<{
    leadId: number;
    title: string;
  } | null>(null);
  const { date, gate } = day;

  useEffect(() => {
    // Fail closed: a role outside the board audience never issues the read
    // (the route 401s it anyway -- this only avoids a pointless request).
    if (!canSeeDayBoard(role)) {
      setState({ kind: 'error', message: 'This day is not available for your role.' });
      return;
    }
    let cancelled = false;
    // THE ONE scoped-query descriptor: exactly one clinic, exactly one day --
    // switching days can never widen into a bulk read.
    // A7 STALE-RESPONSE LAW: rapid previous / next issues several reads; only
    // the response whose request identity is still the latest may paint. The
    // cleanup flag covers unmount; the gate covers OVERLAP.
    const request = dayScopeRequest({ date, clinicId });
    gate.issue(request.key);
    setState({ kind: 'loading' });
    fetchTodayAppointments(request.clinicId ?? undefined, 'board', request.date)
      .then((data) => {
        if (cancelled || !gate.accepts(request.key)) return;
        setState({
          kind: 'ready',
          rows: data.appointments,
          clinicDoctors: data.clinicDoctors ?? [],
          scheduleWindow: data.scheduleWindow ?? null,
          activeClinicId: data.activeClinicId,
        });
      })
      .catch((err: unknown) => {
        if (cancelled || !gate.accepts(request.key)) return;
        // LES-009: the domain message renders VERBATIM.
        const message =
          err instanceof Error && err.message ? err.message : 'Could not load this day.';
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [role, clinicId, date, gate]);

  return (
    <>
      <AppointmentCalendarView
        date={date}
        today={day.today}
        onDateChange={day.selectDate}
        onStepDay={day.stepDay}
        onToday={day.goToday}
        role={role}
        loading={state.kind === 'loading'}
        error={state.kind === 'error' ? state.message : null}
        rows={state.kind === 'ready' ? state.rows : []}
        clinicDoctors={state.kind === 'ready' ? state.clinicDoctors : []}
        scheduleWindow={state.kind === 'ready' ? state.scheduleWindow : null}
        shownClinicLabel={shownClinicLabel}
        canReachAnyClinic={clinicOptions.length > 0}
        activeClinicId={
          state.kind === 'ready'
            ? state.activeClinicId
            : (clinicId ?? clinicOptions[0]?.clinicId ?? null)
        }
        onOpenBlock={(row) => {
          // The EXISTING door only (no new drawer, no second door): the ONE
          // LeadQuickView, exactly as the day board opens it.
          if (rowOpenKind(role, row) === 'lead_quickview' && row.leadId != null) {
            setOpenLead({ leadId: row.leadId, title: dayBoardPatientLabel(row) });
          }
        }}
      />
      {openLead ? (
        <LeadQuickViewHost
          leadId={openLead.leadId}
          seedTitle={openLead.title}
          onClose={() => setOpenLead(null)}
        />
      ) : null}
    </>
  );
}
